import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'

export interface CronLogEntry {
  id: string
  job_name: string
  status: string
  details: Record<string, unknown>
  started_at: string
  completed_at: string | null
  created_at: string
}

export function useCronLog() {
  const [logs, setLogs] = useState<CronLogEntry[]>([])
  const [loading, setLoading] = useState(true)

  const fetchLogs = useCallback(async () => {
    setLoading(true)
    const { data, error } = await supabase
      .from('cron_log')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(50)

    if (error) {
      console.error('Error fetching cron logs:', error)
    } else {
      setLogs(data || [])
    }
    setLoading(false)
  }, [])

  useEffect(() => {
    fetchLogs()

    const channel = supabase
      .channel('cron_log_changes')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'cron_log' }, (payload) => {
        const newEntry = payload.new as CronLogEntry
        setLogs((prev) => [newEntry, ...prev.slice(0, 49)])
      })
      .subscribe()

    return () => {
      channel.unsubscribe()
    }
  }, [fetchLogs])

  return { logs, loading, refetch: fetchLogs }
}

export function useCronStatus() {
  const [nextRun, setNextRun] = useState<string | null>(null)
  const [lastRun, setLastRun] = useState<string | null>(null)
  const [active, setActive] = useState(false)

  const INTERVAL_MINUTES = 240 // 4 hours

  useEffect(() => {
    async function fetchStatus() {
      // Fetch last run from cron_log
      const { data } = await supabase
        .from('cron_log')
        .select('*')
        .eq('job_name', 'fetch-rss-feeds-4h')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle()

      if (data) {
        setLastRun(data.created_at)
        setActive(data.status === 'completed' || data.status === 'running')
      }

      // Calculate next run: every 4 hours from last run
      if (data?.created_at) {
        const lastDate = new Date(data.created_at)
        const nextDate = new Date(lastDate.getTime() + INTERVAL_MINUTES * 60 * 1000)
        const now = new Date()
        if (nextDate < now) {
          // If next run already passed, calculate from now rounded to next 4h
          const hours = now.getHours()
          const nextHour = Math.ceil(hours / 4) * 4
          const calcNext = new Date(now)
          calcNext.setHours(nextHour, 0, 0, 0)
          if (calcNext <= now) {
            calcNext.setHours(calcNext.getHours() + 4)
          }
          setNextRun(calcNext.toISOString())
        } else {
          setNextRun(nextDate.toISOString())
        }
      } else {
        // No runs yet, calculate from now rounded to next 4h
        const now = new Date()
        const hours = now.getHours()
        const nextHour = Math.ceil(hours / 4) * 4
        const calcNext = new Date(now)
        calcNext.setHours(nextHour, 0, 0, 0)
        if (calcNext <= now) {
          calcNext.setHours(calcNext.getHours() + 4)
        }
        setNextRun(calcNext.toISOString())
      }
    }

    fetchStatus()
  }, [])

  return { nextRun, lastRun, active, intervalMinutes: INTERVAL_MINUTES }
}